/* Cookie utilities for getting/setting browser cookies */

export type CookieOptions = {
  days?: number; // Expiration in days from now (default 365)
  path?: string; // Cookie path (default "/")
  domain?: string; // Optional domain
  secure?: boolean; // Force Secure flag (default: based on current protocol)
  sameSite?: "Lax" | "Strict" | "None"; // Default "Lax"
};

/**
 * Get a cookie value by name
 */
export function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const nameEQ = `${name}=`;
  const parts = document.cookie.split(";");
  for (let i = 0; i < parts.length; i++) {
    let c = parts[i];
    while (c.charAt(0) === " ") c = c.substring(1);
    if (c.indexOf(nameEQ) === 0) {
      return decodeURIComponent(c.substring(nameEQ.length));
    }
  }
  return null;
}

/**
 * Set a cookie with common sane defaults.
 * - Defaults to 365 days expiry, path "/",
 * - SameSite=Lax for CSRF protection on top-level navigation,
 * - Secure if current page is served via HTTPS (can be overridden).
 */
export function setCookie(
  name: string,
  value: string,
  options: CookieOptions = {},
): void {
  if (typeof document === "undefined") return;

  const {
    days = 365,
    path = "/",
    domain,
    secure = window.location.protocol === "https:",
    sameSite = "Lax",
  } = options;

  const encodedValue = encodeURIComponent(value);
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();

  let cookie = `${name}=${encodedValue}; Expires=${expires}; Path=${path}; SameSite=${sameSite}`;

  if (domain) cookie += `; Domain=${domain}`;
  if (secure) cookie += `; Secure`;

  document.cookie = cookie;
}
